import posthog from 'posthog-js'

import { needsConsent, storedConsent } from './app/lib/consent'

const projectToken = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN
const host = process.env.NEXT_PUBLIC_POSTHOG_HOST

if (!projectToken || !host) {
  if (process.env.NODE_ENV !== 'production') {
    const missingVariable = !projectToken
      ? 'NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN'
      : 'NEXT_PUBLIC_POSTHOG_HOST'

    throw new Error(
      `${missingVariable} variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once ${missingVariable} is configured`,
    )
  }
} else {
  /* Consent gate, EEA/UK only. Visitors elsewhere are tracked from the
     first pageview with no banner. EEA visitors (resolved at the edge in
     proxy.ts, see app/lib/consent.ts) start opted out with in-memory
     persistence: no events sent and no cookies written until they accept
     the banner (app/components/cookie-banner.tsx). */
  const consent = storedConsent()
  const tracking = consent === 'yes' || (consent === null && !needsConsent())
  const isLocalBuild =
    process.env.NODE_ENV === 'development' ||
    ['localhost', '127.0.0.1'].includes(window.location.hostname)

  posthog.init(projectToken, {
    api_host: host,
    /* api_host is our own reverse proxy (t.payna.com), which is an ingest
       endpoint and not the PostHog app. Without this, links the SDK builds
       back into PostHog, the toolbar's above all, point at the proxy and
       404. */
    ui_host: 'https://us.posthog.com',
    /* Behavior defaults are gated by date inside the SDK. This one buys
       Google Search in-app browser detection, which matters because the
       traffic here is organic search and those visits otherwise land in
       the wrong channel, plus rage-click detection that ignores text
       selection. Deliberately not a later date: 2026-06-25 turns on
       session replay network body streaming, which would put the demo
       form's POST body, names and emails included, into recordings. */
    defaults: '2026-05-30',
    capture_exceptions: true,
    /* TWO independent reasons to drop a $exception, and both are needed.

       1. LOCAL BUILDS. Local development shares the production project. A
          half-saved edit under Fast Refresh throws a ReferenceError that is
          not a bug, and each reload path fingerprints it as a new issue.
          This is NOT covered by the first-party test below: `next dev`
          serves its chunks from /_next/static/ too, so a dev ReferenceError
          carries genuine first-party frames and would sail straight through.
          It is also not covered by `capture_exceptions: false` in dev, which
          would leave the deliberate posthog.captureException in
          app/global-error.tsx still reporting. Init stays on either way, so
          debug logging still works.

       2. NOISE WITH OR WITHOUT A STACK. The public site runs no application
          code that reaches error tracking beyond that global-error capture,
          so every remaining exception is browser or extension noise: an
          opaque cross-origin "Script error." with an empty stack, an
          injected promise rejection, a ResizeObserver loop notice. Keeping
          only exceptions whose stack touches our own code drops all of it
          before ingest.

          "Our own code" means a frame whose file is a first-party bundle,
          served by Next under /_next/static/. Testing for that rather than
          for the mere presence of frames is what catches the noise that DOES
          carry a stack: a browser injects a script and the throw is
          attributed to the document (frame file "/"), or an add-on bundle
          from t.payna.com throws. Neither names a /_next/static/ path.

       /_next/static/ is Next's default asset path; a CDN assetPrefix or a
       basePath would move it, so keep this marker in step with
       next.config.mjs or the filter would drop every real exception. */
    before_send: (event) => {
      if (event?.event === '$exception') {
        if (isLocalBuild) {
          return null
        }
        const exceptions = event.properties?.$exception_list
        const frames = (Array.isArray(exceptions) ? exceptions : []).flatMap(
          (exception) => exception?.stacktrace?.frames ?? [],
        )
        const hitsFirstPartyBundle = frames.some(
          (frame: { filename?: unknown }) =>
            typeof frame?.filename === 'string' &&
            frame.filename.includes('/_next/static/'),
        )
        if (!hitsFirstPartyBundle) {
          return null
        }
      }
      return event
    },
    /* The SDK add-on bundles (session replay recorder, web vitals) load at
       run time from t.payna.com, a different origin to the site. Without
       crossorigin a throw anywhere those bundles sit on the stack is masked
       as a stackless "Script error.", which erases the first-party frames in
       a mixed stack too, so a real error in our code that ran through the
       recorder would be dropped by the filter above. Request them with CORS
       so those frames stay real and the /_next/static/ frames survive. */
    prepare_external_dependency_script: (script) => {
      script.crossOrigin = 'anonymous'
      return script
    },
    debug: process.env.NODE_ENV === 'development',
    opt_out_capturing_by_default: !tracking,
    persistence: tracking ? 'localStorage+cookie' : 'memory',
    /* Core Web Vitals from real visitor devices. The site's traffic is
       organic search, so LCP/CLS/INP are worth measuring in the field
       rather than trusting a lab score. */
    capture_performance: { web_vitals: true },
  })
}
